import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Card, message, Select, Row, Col, Typography, Image, Divider, Modal, QRCode, Carousel, Grid, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addOrder, updateOrder } from '../../store/ordersSlice';
import { fetchProducts } from '../../store/productsSlice';
import { fetchShops } from '../../store/shopsSlice';
import { formatName } from '../../utils/formatters';
import { ShopOutlined, ShoppingCartOutlined, DollarOutlined, QrcodeOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { API_URL, api } from '../../utils/api';

const { Text, Title } = Typography;

const OrderForm = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const sectionStyle = {
    background: isMobile ? 'transparent' : '#f9f9f9',
    padding: isMobile ? 0 : 24,
    borderRadius: 8,
    marginBottom: 24,
  };

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { orders } = useSelector((state) => state.orders);
  const { user } = useSelector((state) => state.auth);
  const { shops } = useSelector((state) => state.shops);
  const { products, error: productsError } = useSelector((state) => state.products);
  const [selectedShopImage, setSelectedShopImage] = useState(null);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState('CASH ON DELIVERY');
  const [selectedProductImages, setSelectedProductImages] = useState([]);
  const [activeQR, setActiveQR] = useState(null);

  useEffect(() => {
    const fetchActiveQR = async () => {
      try {
        const response = await api.get('/qrcodes/active');
        setActiveQR(response.data);
      } catch (error) {
        console.error('Failed to fetch active QR:', error);
      }
    };
    fetchActiveQR();
  }, []);

  const [selectedPincode, setSelectedPincode] = useState(null);

  // Watch values for calculating totals and enabling/disabling fields
  const watchedShopId = Form.useWatch('shopId', form);
  const watchedItems = Form.useWatch('items', form);
  // Using Form state for values (auto-calculated)
  // Fields are disabled so manual edits are not possible, but values are stored in form.

  const isEditMode = !!orderId;

  // Get user's working pincodes
  const userWorkingPincodes = React.useMemo(() => {
    const wp = user?.workingPincodes;
    if (!wp) return [];
    return Array.isArray(wp)
      ? wp.map(String)
      : String(wp).split(',').map(p => p.trim()).filter(Boolean);
  }, [user]);

  // Determine available pincodes for dropdown
  const pincodeOptions = React.useMemo(() => {
    const options = new Set();
    
    // Add user working pincodes
    userWorkingPincodes.forEach(p => options.add(String(p)));

    // Add pincodes from shops visible to the user
    shops.forEach(s => {
      if (String(s.status).toLowerCase() === 'verified') {
        const isMyShop = s.bdeId === user?.id;
        const isInMyArea = userWorkingPincodes.length === 0 || userWorkingPincodes.includes(String(s.pincode));
        
        if (isMyShop || isInMyArea) {
           if (s.pincode) options.add(String(s.pincode));
        }
      }
    });

    return [...options].sort();
  }, [userWorkingPincodes, shops, user]);

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchShops());
  }, [dispatch]);

  useEffect(() => {
    if (productsError) {
      message.error(`Failed to load products: ${productsError}`);
    }
  }, [productsError]);

  useEffect(() => {
    if (isEditMode) {
      const order = orders.find((o) => o.id === orderId);
      if (order) {
        if (order.bdeId !== user.id) {
          message.error("You don't have permission to edit this order");
          navigate('/bde/orders');
          return;
        }
        if (order.status === 'Verified' || order.status === 'Completed') {
          message.info('This order cannot be edited after verification or completion');
          navigate(`/bde/orders/details/${order.id}`);
          return;
        }
        let pm = order.paymentMethod || 'CASH ON DELIVERY';
        if (pm === 'CASH') pm = 'CASH ON DELIVERY';
        if (pm === 'ONLINE') pm = 'PREPAID';

        // Map legacy single items to new items array if needed
        let initialItems = order.items || [];
        if (initialItems.length === 0 && order.productName) {
          initialItems = [{
            brandName: order.brandName,
            productName: order.productName,
            volumeMl: order.volumeMl,
            mrp: order.mrp,
            quantity: order.quantity || 1,
            amount: order.billingPrice // approximation for legacy
          }];
        }

        form.setFieldsValue({
          ...order,
          items: initialItems,
          paymentMethod: pm,
        });
        setCurrentPaymentMethod(pm);
        // If editing an existing order, assume payment was already handled/verified if it exists
        if (pm === 'PREPAID' || order.paymentMethod === 'ONLINE') {
          setPaymentVerified(true);
        }

        // Set initial images
        if (shops.length > 0) {
          const shop = shops.find(s => s.id === order.shopId);
          setSelectedShopImage(shop?.boardImageUrl || null);
        }

        // Update product images
        if (initialItems && products.length) {
          const images = initialItems.map(item => {
            const prod = products.find(p => p.name === item.productName);
            return prod?.image || null;
          }).filter(Boolean);
          setSelectedProductImages(images);
        }

      } else {
        message.error('Order not found');
        navigate('/bde/orders');
      }
    }
  }, [orderId, orders, form, navigate, user.id, isEditMode, shops, products]);

  // Update product images based on current items




  // Recalculate totals and update images whenever items change
  useEffect(() => {
    // Debugging logs
    // console.log('Products:', products);
    // console.log('Watched Items:', watchedItems);

    // Only proceed if we have items and products loaded
    if (watchedItems && products.length > 0) {
      let total = 0;
      const updatedImages = [];

      watchedItems.forEach(item => {
        if (item && item.productName) {
          const prod = products.find(p => p.name === item.productName);
          if (prod) {
            // console.log(`Found product for ${item.productName}:`, prod);
            if (prod.image) {
              updatedImages.push(prod.image);
            } else {
              // console.log(`Product ${item.productName} has no image`);
            }
          }
          // Calculation: Price * Quantity (if available in item, else fallback)
          total += (Number(item?.amount) || 0);
        }
      });

      // Avoid clearing images if unrelated updates happen, but here we MUST sync with items
      // console.log('Updated Images List:', updatedImages);
      setSelectedProductImages(updatedImages);

      // Auto-calculate totals from items
      const newMrpTotal = watchedItems.reduce((sum, item) => sum + (Number(item?.mrp) || 0), 0);
      const newSubtotal = total;
      const newProfitMargin = newMrpTotal - newSubtotal;

      // Only update if not already set or if items changed significantly (simple check)
      // Actually, we should update whenever items change, unless user manually edited?
      // For now, let's keep it auto-updating to prevent stale data. 
      // User can edit AFTER items are settled.
      form.setFieldsValue({ 
        billingPrice: total,
        mrpTotal: newMrpTotal,
        subtotal: newSubtotal,
        profitMargin: newProfitMargin
      });
    }
  }, [watchedItems, products, form]);


  const onFinish = (values) => {
    const selectedShop = shops.find(s => s.id === values.shopId);

    // Calculate total values from items
    const items = values.items.map(item => ({
      ...item,
      brandName: formatName(item.brandName),
      productName: item.productName, // Keep original casing from DB/Dropdown
      volumeMl: Number(item.volumeMl) || 0,
      mrp: Number(item.mrp) || 0,
      quantity: Number(item.quantity) || 1,
      amount: Number(item.amount) || 0
    }));

    // Use the editable subtotal as the billing price
    const billingPrice = Number(values.subtotal) || 0;

    // Validation: Best Price must be min 1000
    if (billingPrice < 1000) {
      message.error('Order total must be at least ₹1000.');
      return;
    }

    const totalMrp = Number(values.mrpTotal) || items.reduce((sum, item) => sum + (item.mrp || 0), 0);
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

    // Use first item for legacy display fields if needed, or aggregate
    const mainItem = items[0] || {};

    const baseValues = {
      ...values,
      items,
      shopId: values.shopId,
      customerName: selectedShop?.name || undefined,
      gstIn: values.gstIn,
      amount: billingPrice, // The final bill amount
      billingPrice: billingPrice,
      discount: 0,

      // Legacy fields (populate with first item or aggregate info)
      brandName: mainItem.brandName || "Multiple",
      productName: items.length > 1 ? "Multiple Products" : mainItem.productName,
      volumeMl: mainItem.volumeMl,
      mrp: totalMrp,
      quantity: totalQuantity,
    };

    const formData = new FormData();
    Object.keys(baseValues).forEach(key => {
      if (key === 'items') {
        formData.append(key, JSON.stringify(baseValues[key]));
      } else if (key === 'paymentProof') {
        // Handle separately
      } else if (baseValues[key] !== undefined && baseValues[key] !== null) {
        formData.append(key, baseValues[key]);
      }
    });

    if (values.paymentProof && values.paymentProof.length > 0) {
      if (values.paymentProof[0].originFileObj) {
        formData.append('paymentProof', values.paymentProof[0].originFileObj);
      }
    }

    if (isEditMode) {
      const originalOrder = orders.find((o) => o.id === orderId);
      formData.append('id', originalOrder.id);
      
      dispatch(updateOrder(formData))
        .unwrap()
        .then(() => {
          message.success('Order updated successfully');
          navigate('/bde/orders');
        })
        .catch((err) => {
          const errorMsg = err.message || (typeof err === 'string' ? err : 'Unknown error occurred');
          message.error(`Failed to update order: ${errorMsg}`);
        });
    } else {
      const newId = `ORD-${Date.now()}`;
      formData.append('id', newId);
      formData.append('bdeId', user.id);
      formData.append('status', 'Pending');
      formData.append('createdAt', new Date().toISOString().split('T')[0]);
      
      dispatch(addOrder(formData))
        .unwrap()
        .then(() => {
          message.success('Order created successfully');
          navigate('/bde/orders');
        })
        .catch((err) => {
          const errorMsg = err.message || (typeof err === 'string' ? err : 'Unknown error occurred');
          message.error(`Failed to create order: ${errorMsg}`);
        });
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues.shopId) {
      const shop = shops.find(s => s.id === changedValues.shopId);
      setSelectedShopImage(shop?.boardImageUrl || null);
      if (shop && shop.gstNo) {
        form.setFieldsValue({ gstIn: shop.gstNo });
      } else {
        form.setFieldsValue({ gstIn: '' });
      }
    }
    if (changedValues.paymentMethod) {
      setCurrentPaymentMethod(changedValues.paymentMethod);
      if (changedValues.paymentMethod === 'PREPAID') {
        setPaymentVerified(false);
      } else {
        setPaymentVerified(true);
      }
    }

    // Handle manual edits to financial fields - DISABLED
    // if ('mrpTotal' in changedValues || 'subtotal' in changedValues || 'profitMargin' in changedValues) {
    //   const currentMrp = Number(allValues.mrpTotal) || 0;
    //   const currentSubtotal = Number(allValues.subtotal) || 0;
    //   const currentProfit = Number(allValues.profitMargin) || 0;

    //   // Logic: Profit = MRP - Subtotal
    //   // If MRP changes, recalculate Profit (assuming Subtotal is fixed unless explicitly changed)
    //   // If Subtotal changes, recalculate Profit
    //   // If Profit changes, recalculate Subtotal (assuming MRP is fixed)

    //   if ('mrpTotal' in changedValues) {
    //      form.setFieldsValue({ profitMargin: currentMrp - currentSubtotal });
    //   }
    //   if ('subtotal' in changedValues) {
    //      form.setFieldsValue({ profitMargin: currentMrp - currentSubtotal });
    //   }
    //   if ('profitMargin' in changedValues) {
    //      form.setFieldsValue({ subtotal: currentMrp - currentProfit });
    //   }
    // }
  };

  // Helper to handle item detail changes
  const handleItemChange = (index, field, value) => {
    const items = form.getFieldValue('items') || [];
    const currentItem = { ...items[index] };

    if (field === 'productName') {
      const product = products.find(p => p.name === value);
      if (product) {
        currentItem.productName = value;
        currentItem.volumeMl = product.ml;

        // Use discountedPrice if available, else regular price
        const unitPrice = product.discountedPrice || product.price;

        // Default quantity 1 logic
        const qty = currentItem.quantity || 1;
        currentItem.mrp = product.price * qty; // MRP is always original price
        currentItem.amount = unitPrice * qty; // Amount is based on effective unit price (discounted or original)

        const newItems = [...items];
        newItems[index] = currentItem;
        form.setFieldsValue({ items: newItems });
      }
    } else if (field === 'quantity') {
      const qty = Number(value) || 1;
      const product = products.find(p => p.name === currentItem.productName);
      if (product) {
        currentItem.quantity = qty;

        // Use discountedPrice if available, else regular price
        const unitPrice = product.discountedPrice || product.price;

        currentItem.mrp = product.price * qty; // MRP always original
        currentItem.amount = unitPrice * qty; // Amount uses effective price

        const newItems = [...items];
        newItems[index] = currentItem;
        form.setFieldsValue({ items: newItems });
      }
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentVerified(true);
    setIsPaymentModalVisible(false);
    message.success('Payment marked as collected');
  };

  const getImageSrc = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;

    // Robust URL construction
    try {
      // Remove leading slash if present to avoid double slash confusion with base
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      // Construct absolute URL
      const url = new URL(cleanPath, API_URL);
      return url.toString();
    } catch (e) {
      console.error("URL Construction Error:", e);
      return `${API_URL}${path}`;
    }
  };

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 0 : '0 12px' }}>
      <style>{`
        .ant-input-number-disabled, .ant-input-disabled, .ant-select-disabled .ant-select-selection-item {
          color: #000 !important;
          opacity: 1 !important;
          -webkit-text-fill-color: #000 !important;
          font-weight: 500 !important;
        }
        .ant-input-number-disabled input {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
        }
      `}</style>
      <Card
        variant="borderless"
        style={{ 
          boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.05)', 
          borderRadius: isMobile ? 0 : 12 
        }}
        bodyStyle={{ padding: isMobile ? 12 : 24 }}
        title={<Title level={4} style={{ margin: 0 }}>{isEditMode ? 'Edit Order' : 'Create New Order'}</Title>}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={handleValuesChange}
          initialValues={{
            items: [{ brandName: "Shark Fragrance", quantity: 1 }] // Initial row
          }}
          size="large"
        >
          <Row gutter={isMobile ? [8, 8] : [32, 24]}>
            {/* Left Column: Form Fields */}
            <Col xs={24} lg={16}>

              {/* Shop Section */}
              <div style={{ ...sectionStyle, marginBottom: isMobile ? 12 : 24 }}>
                <Divider orientation="left" style={{ margin: isMobile ? '0 0 12px 0' : '0 0 24px 0', fontSize: isMobile ? 16 : 18 }}>
                  <ShopOutlined /> Shop Selection
                </Divider>
                <Form.Item label="Select Pincode">
                  <Select
                    placeholder="Filter by pincode"
                    allowClear
                    onChange={(value) => {
                      setSelectedPincode(value);
                      form.setFieldsValue({ shopId: undefined });
                    }}
                  >
                    {pincodeOptions.map(pincode => (
                      <Select.Option key={pincode} value={pincode}>{pincode}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="shopId"
                  label="Select Shop"
                  rules={[{ required: true, message: 'Please select a shop!' }]}
                >
                  <Select placeholder="Select shop by working pincode" showSearch optionFilterProp="children">
                    {shops
                      .filter(s => {
                        if (selectedPincode && String(s.pincode) !== String(selectedPincode)) return false;
                        if (String(s.status).toLowerCase() !== 'verified') return false;
                        // Show shop if it matches working pincodes OR if it was created by this BDE
                        if (s.bdeId === user.id) return true;
                        return userWorkingPincodes.length === 0 ? true : userWorkingPincodes.includes(String(s.pincode));
                      })
                      .map(s => (
                        <Select.Option key={s.id} value={s.id}>
                          {s.name} - {s.ownerName} ({s.pincode})
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
                <Form.Item name="gstIn" label="GSTIN">
                  <Input placeholder="Auto-filled from shop" />
                </Form.Item>
              </div>

              {/* Products Section */}
              <div style={{ ...sectionStyle, marginBottom: isMobile ? 12 : 24 }}>
                <Divider orientation="left" style={{ margin: isMobile ? '0 0 12px 0' : '0 0 24px 0', fontSize: isMobile ? 16 : 18 }}>
                  <ShoppingCartOutlined /> Products
                </Divider>

                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Card
                          key={key}
                          size="small"
                          style={{ marginBottom: isMobile ? 8 : 16, background: '#fff' }}
                          extra={fields.length > 1 && (
                            <DeleteOutlined onClick={() => remove(name)} style={{ color: 'red', cursor: 'pointer' }} />
                          )}
                        >
                          <Row gutter={16}>
                            <Col xs={24} md={8}>
                              <Form.Item
                                {...restField}
                                name={[name, 'brandName']}
                                label="Brand Name"
                                rules={[{ required: true, message: 'Missing brand name' }]}
                              >
                                <Input placeholder="Brand" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                              <Form.Item
                                {...restField}
                                name={[name, 'productName']}
                                label="Product"
                                rules={[{ required: true, message: 'Missing product' }]}
                              >
                                <Select
                                  placeholder="Select product"
                                  showSearch
                                  optionFilterProp="children"
                                  onChange={(val) => handleItemChange(index, 'productName', val)}
                                >
                                  {products.map(p => {
                                    const isSelectedElsewhere = watchedItems?.some((item, idx) => 
                                      idx !== index && item?.productName === p.name
                                    );
                                    const isOutOfStock = (p.stock || 0) <= 0;

                                    return (
                                      <Select.Option 
                                        key={p._id} 
                                        value={p.name}
                                        disabled={isSelectedElsewhere || isOutOfStock}
                                      >
                                        {p.name} {isOutOfStock ? '(Out of Stock)' : ''}
                                        {isSelectedElsewhere ? ' (Selected)' : ''}
                                      </Select.Option>
                                    );
                                  })}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'volumeMl']}
                                label="ML"
                              >
                                <InputNumber style={{ width: '100%' }} disabled controls={false} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'quantity']}
                                label="Qty"
                                rules={[
                                  { required: true, message: 'Required' },
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      const productName = getFieldValue(['items', index, 'productName']);
                                      const product = products.find(p => p.name === productName);
                                      if (product && value > product.stock) {
                                        return Promise.reject(new Error(`Max stock: ${product.stock}`));
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <InputNumber
                                  min={1}
                                  max={products.find(p => p.name === watchedItems?.[index]?.productName)?.stock}
                                  style={{ width: '100%' }}
                                  onChange={(val) => handleItemChange(index, 'quantity', val)}
                                  controls={false}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'mrp']}
                                label="MRP (Total)"
                              >
                                <InputNumber
                                  style={{ width: '100%' }}
                                  formatter={value => {
                                    if (!value) return '';
                                    const num = Number(value);
                                    if (isNaN(num)) return `₹ ${value}`;
                                    return num % 1 !== 0 ? `₹ ${num.toFixed(2)}` : `₹ ${num}`;
                                  }}
                                  readOnly
                                  disabled
                                  controls={false}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'amount']}
                                label="Discounted Price"
                                rules={[{ required: true, message: 'Required' }]}
                              >
                                <InputNumber
                                  style={{ width: '100%' }}
                                  min={0}
                                  formatter={value => {
                                    if (!value) return '';
                                    const num = Number(value);
                                    if (isNaN(num)) return `₹ ${value}`;
                                    return num % 1 !== 0 ? `₹ ${num.toFixed(2)}` : `₹ ${num}`;
                                  }}
                                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                                  readOnly
                                  disabled
                                  controls={false}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Form.Item>
                        <Button 
                          type="dashed" 
                          onClick={() => add({ brandName: "Shark Fragrance", quantity: 1 })} 
                          block 
                          icon={<PlusOutlined />}
                          disabled={products.every(p => 
                            (p.stock || 0) <= 0 || watchedItems?.some(item => item?.productName === p.name)
                          )}
                        >
                          Add Another Product
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>

              {/* Pricing Section */}
              <div style={{ ...sectionStyle, marginBottom: isMobile ? 24 : 0 }}>
                <Divider orientation="left" style={{ margin: '0 0 24px 0', fontSize: 18 }}>
                  <DollarOutlined /> Order Financials
                </Divider>
                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item label="MRP Total" name="mrpTotal">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => {
                          if (value === null || value === undefined) return '';
                          const num = Number(value);
                          if (isNaN(num)) return `₹ ${value}`;
                          const formatted = num % 1 !== 0 ? num.toFixed(2) : num.toString();
                          return `₹ ${formatted}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }}
                        readOnly
                        disabled
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Profit Margin" name="profitMargin">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => {
                          if (value === null || value === undefined) return '';
                          const num = Number(value);
                          if (isNaN(num)) return `₹ ${value}`;
                          const formatted = num % 1 !== 0 ? num.toFixed(2) : num.toString();
                          return `₹ ${formatted}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }}
                        readOnly
                        disabled
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Subtotal" name="subtotal">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => {
                          if (value === null || value === undefined) return '';
                          const num = Number(value);
                          if (isNaN(num)) return `₹ ${value}`;
                          const formatted = num % 1 !== 0 ? num.toFixed(2) : num.toString();
                          return `₹ ${formatted}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }}
                        readOnly
                        disabled
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="paymentMethod"
                      label="Payment Method"
                      rules={[{ required: true, message: 'Please select payment method!' }]}
                      initialValue="CASH ON DELIVERY"
                    >
                      <Select
                        placeholder="Select payment method"
                        disabled={!watchedShopId || !watchedItems || watchedItems.length === 0}
                      >
                        <Select.Option value="CASH ON DELIVERY">CASH ON DELIVERY</Select.Option>
                        <Select.Option value="PREPAID">PREPAID</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="paymentProof"
                      label="Payment Proof"
                      rules={[{ required: currentPaymentMethod === 'PREPAID', message: 'Please upload payment proof!' }]}
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e;
                        return e?.fileList;
                      }}
                    >
                      <Upload
                        beforeUpload={() => false}
                        listType="picture"
                        maxCount={1}
                        accept="image/*"
                      >
                        <Button icon={<UploadOutlined />}>Upload Payment Proof</Button>
                      </Upload>
                    </Form.Item>

                    {currentPaymentMethod === 'PREPAID' && (
                      <div style={{ marginTop: 8, marginBottom: 24 }}>
                        {paymentVerified ? (
                          <div style={{ color: '#52c41a', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CheckCircleOutlined style={{ fontSize: 20 }} />
                            <Text strong type="success">Payment Verified</Text>
                            <Button size="small" type="link" onClick={() => setIsPaymentModalVisible(true)}>
                              View QR Again
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="primary"
                            icon={<QrcodeOutlined />}
                            onClick={() => setIsPaymentModalVisible(true)}
                            style={{ background: '#1890ff' }}
                          >
                            Take Payment
                          </Button>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Right Column: Visuals */}
            <Col xs={24} lg={8}>
              <div style={{ position: 'sticky', top: 24 }}>
                <Card
                  title="Shop Image"
                  variant="borderless"
                  style={{ 
                    marginBottom: 24, 
                    borderRadius: isMobile ? 0 : 8, 
                    boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.05)' 
                  }}
                  styles={{ body: { padding: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 250, background: '#fafafa' } }}
                >
                  {selectedShopImage ? (
                    <Image
                      src={getImageSrc(selectedShopImage)}
                      alt="Shop"
                      style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                      fallback="https://placehold.co/200x200?text=No+Image"
                      preview={false}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <ShopOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 12 }} />
                      <br />
                      <Text type="secondary">Select a shop to view image</Text>
                    </div>
                  )}
                </Card>

                <Card
                  title="Product Images"
                  variant="borderless"
                  style={{ 
                    borderRadius: isMobile ? 0 : 8, 
                    boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.05)' 
                  }}
                  styles={{ body: { padding: 0, minHeight: 250, background: '#fafafa' } }}
                >
                  {selectedProductImages.length > 0 ? (
                    <Carousel
                      autoplay
                      draggable
                      arrows
                      swipeToSlide
                      style={{ width: '100%', overflow: 'hidden', borderRadius: 8, cursor: 'grab' }}
                    >
                      {selectedProductImages.map((src, idx) => (
                        <div key={idx}>
                          <div style={{
                            height: 300, // Increased height for better visibility
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: '#f0f0f0',
                            overflow: 'hidden' // Ensure no spillover
                          }}>
                            <Image
                              src={getImageSrc(src)}
                              alt={`Product ${idx + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover', // Ensures full width/height coverage (might crop)
                                display: 'block'
                              }}
                              fallback="https://placehold.co/300x300?text=Load+Error"
                              preview={false}
                            />
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <ShoppingCartOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 12 }} />
                      <br />
                      <Text type="secondary">Select products to view images</Text>
                    </div>
                  )}
                </Card>

                <div style={{ marginTop: 24 }}>
                  <Form.Item shouldUpdate={(prev, curr) => 
                    prev.subtotal !== curr.subtotal || 
                    prev.paymentMethod !== curr.paymentMethod || 
                    paymentVerified // Simple boolean toggle check
                  }>
                    {() => (
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        style={{ height: 48, fontSize: 16, marginBottom: 12 }}
                        disabled={(currentPaymentMethod === 'PREPAID' && !paymentVerified) || (Number(form.getFieldValue('subtotal')) || 0) < 1000}
                      >
                        {isEditMode ? 'Update Order' : 'Create Order'}
                      </Button>
                    )}
                  </Form.Item>
                  <Button size="large" block onClick={() => navigate('/bde/orders')}>
                    Cancel
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      <Modal
        title="Scan QR Code to Pay"
        open={isPaymentModalVisible}
        onCancel={() => setIsPaymentModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPaymentModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handlePaymentSuccess}>
            Payment Collected
          </Button>,
        ]}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24 }}>
          {activeQR ? (
            <div style={{ textAlign: 'center' }}>
              <Image
                src={`${API_URL}${activeQR.imageUrl}`}
                alt={activeQR.name}
                width={250}
                preview={false}
              />
              <div style={{ marginTop: 8 }}>
                <Text strong>{activeQR.name}</Text>
              </div>
            </div>
          ) : (
            <QRCode
              value={`upi://pay?pa=example@upi&pn=B2B Sales&am=${form.getFieldValue('subtotal') || 0}&cu=INR`}
              size={250}
              icon="https://gw.alipayobjects.com/zos/rmsportal/KDpgvguMpGfqaHPjicRK.svg"
            />
          )}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text strong style={{ fontSize: 18 }}>Amount: ₹{form.getFieldValue('subtotal') || 0}</Text>
            <br />
            <Text type="secondary">Ask customer to scan this QR code</Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderForm;
